<template>
	<div>
		<!-- Label -->
		<div
			v-if="label"
			class="flex flex-wrap items-start justify-between mb-2 "
		>
			<div class="flex items-center">
				<label :for="resolvedInputId" class="mb-0">
					{{ label }}
				</label>
				<span
					v-if="required"
					class="ml-1 text-lg font-bold leading-none text-red-400"
				>
					*
				</span>
			</div>
			<slot name="label-action" />
		</div>

		<!-- Input -->
		<div class="relative">
			<input
				ref="input"
				:type="showPassword ? 'text' : type"
				:name="name"
				class="w-full px-3 transition-all duration-200 border rounded-lg outline-none ring-2 focus:ring-neutral-200 ring-transparent placeholder:text-neutral-400 focus:border-neutral-500 border-neutral-200 h-11"
				:class="[
					{
						'border-red-400': errorMessage,
						'pl-10': leadingIcon,
						'pr-10': clearable || isPassword
					},
					inputClass
				]"
				:id="resolvedInputId"
				:tabindex="inputTabindexAttr"
				:placeholder="placeholder"
				:value="value"
				@input="$emit('input', $event.target.value)"
				@focus="$emit('focus', $event)"
				@blur="$emit('blur', $event.target.value)"
				@keypress="$emit('keypress', $event)"
				:autocomplete="
					autocomplete
						? typeof autocomplete === 'boolean'
							? 'on'
							: autocomplete
						: 'off'
				"
				:autofocus="autofocus"
				:required="required"
				:disabled="disabled"
				:readonly="readonly"
				:aria-label="ariaLabel"
				:aria-invalid="errorMessage ? 'true' : null"
				:aria-describedby="
					errorMessage ? `${resolvedInputId}-error` : null
				"
				:step="step"
				:min="min"
				:max="max"
				:inputmode="inputmode || (type === 'number' ? 'decimal' : null)"
			/>

			<!-- Leading icon -->
			<Component
				v-if="leadingIcon"
				:is="leadingIcon"
				class="absolute -translate-y-1/2 pointer-events-none text-neutral-400 left-3 top-1/2"
			/>

			<!-- Clear input button -->
			<button
				v-if="isClearable && !isPassword"
				type="button"
				class="absolute flex items-center justify-center w-8 h-8 -translate-y-1/2 right-2 top-1/2"
				@click="clearInput"
				tabindex="-1"
				:aria-label="$t('input.clear-input')"
			>
				<IconTimes size="16" />
			</button>

			<!-- Show password button -->
			<Tooltip
				v-if="isPassword"
				:content="
					showPassword
						? $t('input.hide-password')
						: $t('input.show-password')
				"
				placement="top"
				class="absolute -translate-y-1/2 right-2 top-1/2"
			>
				<button
					type="button"
					class="flex items-center justify-center w-8 h-8 transform translate-x-px"
					@click="togglePasswordState"
					:tabindex="passwordToggleTabindex"
					:aria-pressed="showPassword.toString()"
					:aria-label="
						showPassword
							? $t('input.hide-password')
							: $t('input.show-password')
					"
				>
					<Component
						:is="showPasswordStateIcon"
						size="16"
						aria-hidden="true"
					/>
				</button>
			</Tooltip>
		</div>

		<!-- Validation errors -->
		<div
			v-if="errorMessage"
			:id="`${resolvedInputId}-error`"
			class="mt-1.5 text-sm text-red-500"
		>
			{{ errorMessage }}
		</div>
	</div>
</template>

<script>
import { IconEye, IconEyeOff, IconTimes } from '~/components/Icons'
import { Tooltip } from '~/components/Tooltip'

export default {
	name: 'BaseInput',
	components: {
		IconEye,
		IconEyeOff,
		IconTimes,
		Tooltip
	},
	props: {
		label: {
			type: String,
			default: null
		},
		name: {
			type: String,
			default: null
		},
		inputId: {
			type: String,
			default: null
		},
		placeholder: {
			type: [String, Number],
			default: null
		},
		value: {
			default: null
		},
		type: {
			type: String,
			default: 'text'
		},
		tabindex: {
			type: [Number, String],
			default: null
		},
		passwordToggleTabindex: {
			type: [Number, String],
			default: 0
		},
		autocomplete: {
			type: [Boolean, String],
			default: false
		},
		autofocus: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		readonly: {
			type: Boolean,
			default: false
		},
		ariaLabel: {
			type: String,
			default: null
		},
		step: {
			type: [Number, String],
			default: null
		},
		min: {
			type: [Number, String],
			default: null
		},
		max: {
			type: [Number, String],
			default: null
		},
		inputmode: {
			type: String,
			default: null
		},
		inputClass: {
			type: [String, Array, Object],
			default: ''
		},
		validationMessage: {
			type: String,
			default: null
		},
		error: {
			type: String,
			default: null
		},
		leadingIcon: {
			type: [Object, Function],
			default: null
		},
		clearable: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showPassword: false
		}
	},
	computed: {
		nameToId() {
			return this.name ? this.name.toLowerCase() : ''
		},
		resolvedInputId() {
			return this.inputId || `input-${this.nameToId}`
		},
		isPassword() {
			return this.type === 'password'
		},
		showPasswordStateIcon() {
			const icons = {
				show: IconEye,
				hide: IconEyeOff
			}

			return this.showPassword ? icons['hide'] : icons['show']
		},
		errorMessage() {
			return this.error || this.validationMessage
		},
		inputTabindexAttr() {
			if (
				this.tabindex === null ||
				typeof this.tabindex === 'undefined'
			) {
				return null
			}

			return String(this.tabindex)
		},
		isClearable() {
			return this.clearable && this.value
		}
	},
	methods: {
		togglePasswordState() {
			this.showPassword = !this.showPassword
		},
		clearInput() {
			this.$emit('input', '')
			this.$refs.input?.focus()
		}
	}
}
</script>
